import React, { useState, useEffect } from 'react';
import { supabase, authHelpers } from '../global/supabase';
import { CLIENT_ID } from '../config';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function Users({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterBy, setFilterBy] = useState('all');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    stateLicenseNumber: '',
    pin: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyCreateForm = {
    firstName: '',
    lastName: '',
    email: '',
    stateLicenseNumber: '',
    pin: '',
    password: '',
    certificationLevel: '',
    isEditor: false,
    isAdmin: false
  };
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Fetch all users (admin-only RPC; never returns the PIN)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_all_users');

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  // Handle cert level change
  const handleCertLevelChange = async (userId, newValue) => {
    try {
      const { error } = await supabase.rpc('update_user_metadata', {
        user_id: userId,
        first_name: null,
        last_name: null,
        state_license_number: null,
        user_pin: null,
        is_editor: null,
        is_admin: null,
        certification_level: newValue
      });

      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, certification_level: newValue || null }
          : user
      ));
    } catch (error) {
      console.error('Error updating certification level:', error);
      alert('Error updating certification level: ' + error.message);
    }
  };

  // Toggle user permission
  const togglePermission = async (userId, permissionType, currentValue) => {
    try {
      const updateData = {
        user_id: userId,
        first_name: null,
        last_name: null,
        state_license_number: null,
        user_pin: null,
        is_editor: permissionType === 'editor' ? !currentValue : null,
        is_admin: permissionType === 'admin' ? !currentValue : null,
        certification_level: null
      };

      const { error } = await supabase.rpc('update_user_metadata', updateData);

      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              [permissionType === 'editor' ? 'is_editor' : 'is_admin']: !currentValue 
            }
          : user
      ));

    } catch (error) {
      console.error('Error updating permission:', error);
      alert('Error updating permission: ' + error.message);
    }
  };

  // Update user license info
  const updateLicenseInfo = async () => {
    if (!editingUser) return;
    if (formData.pin && !/^\d{6}$/.test(formData.pin)) {
      alert('PIN must be exactly 6 digits');
      return;
    }
    
    try {
      const { error } = await supabase.rpc('update_user_metadata', {
        user_id: editingUser.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        state_license_number: formData.stateLicenseNumber,
        // Blank keeps the current PIN; the server hashes a new one.
        user_pin: formData.pin || null,
        is_editor: null,
        is_admin: null,
        certification_level: null
      });

      if (error) throw error;

      alert('License info updated successfully!');
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating license info:', error);
      alert('Error updating license info: ' + error.message);
    }
  };

  // Open license info modal
  const openLicenseModal = (user) => {
    setEditingUser(user);
    setFormData({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      stateLicenseNumber: user.state_license_number || '',
      pin: ''
    });
    setShowModal(true);
  };

  // Activate / deactivate a user. Deactivating blocks login and secondary-
  // witness use WITHOUT deleting any inventory or other records they entered.
  const setUserActive = async (user, active) => {
    if (!active) {
      const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
      const confirmed = window.confirm(
        `Deactivate ${name}?\n\n` +
        `They will no longer be able to log in or be used as a secondary witness. ` +
        `All records they have already entered are kept and remain unchanged. ` +
        `You can reactivate the account at any time.`
      );
      if (!confirmed) return;
    }

    try {
      const { error } = await supabase.rpc('set_user_active', {
        target_user_id: user.id,
        active
      });

      if (error) throw error;

      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, is_active: active } : u
      ));
    } catch (error) {
      console.error('Error updating active status:', error);
      alert('Error updating user status: ' + error.message);
    }
  };

  // Open the create-user modal with a clean form
  const openCreateModal = () => {
    setCreateForm(emptyCreateForm);
    setShowCreateModal(true);
  };

  // Create a new, pre-confirmed account (admin-create-user edge function)
  const createUser = async () => {
    const firstName = createForm.firstName.trim();
    const lastName = createForm.lastName.trim();
    const email = createForm.email.trim();
    const stateLicenseNumber = createForm.stateLicenseNumber.trim();
    const { pin, password } = createForm;

    if (!firstName || !lastName || !email || !stateLicenseNumber) {
      alert('Please fill in all required fields');
      return;
    }
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      alert('PIN must be exactly 6 digits');
      return;
    }

    try {
      setCreating(true);

      const metadata = {
        first_name: firstName,
        last_name: lastName,
        location: CLIENT_ID,
        state_license_number: stateLicenseNumber,
        pin
      };
      if (createForm.certificationLevel) {
        metadata.certificationLevel = createForm.certificationLevel;
      }

      // Roles travel separately: the function puts them in app_metadata.
      const { data, error } = await authHelpers.adminCreateUser(email, password, metadata, {
        isAdmin: createForm.isAdmin,
        isEditor: createForm.isEditor
      });

      if (error) throw error;
      if (!data?.user) throw new Error('User was not created');

      alert('User created successfully!');
      setShowCreateModal(false);
      setCreateForm(emptyCreateForm);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = users
    .filter(user => {
      const searchMatch = searchTerm === '' || 
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.state_license_number?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const roleMatch = filterBy === 'all' ||
        (filterBy === 'admins' && user.is_admin) ||
        (filterBy === 'editors' && user.is_editor && !user.is_admin) ||
        (filterBy === 'users' && !user.is_editor && !user.is_admin);
      
      return searchMatch && roleMatch;
    })
    .sort((a, b) => {
      let aValue = '';
      let bValue = '';
      
      switch (sortBy) {
        case 'first_name':
          aValue = a.first_name || '';
          bValue = b.first_name || '';
          break;
        case 'last_name':
          aValue = a.last_name || '';
          bValue = b.last_name || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'created_at':
          aValue = a.created_at || '';
          bValue = b.created_at || '';
          break;
        default:
          aValue = a.first_name || '';
          bValue = b.first_name || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '20px' }}>
      <HamburgerMenu 
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div style={{ maxWidth: '1400px', margin: '0 auto', paddingTop: '20px' }}>
        {/* Header with Back Button */}
        <div style={{ textAlign: 'center', marginBottom: '40px', marginTop: '30px'}}>
          <button 
            onClick={() => onNavigate('admin-dashboard')}
            style={{
              position: 'absolute',
              left: '20px',
              top: '20px',
              padding: '10px 20px',
              border: '2px solid #d32f2f',
              borderRadius: '5px',
              background: 'white',
              color: '#d32f2f',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#d32f2f';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#d32f2f';
            }}
          >
            ← Back to Admin
          </button>
          
          <h1 style={{ color: '#333', fontSize: '28px', fontWeight: '600', marginBottom: '10px' }}>
            User Management
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Toggle permissions instantly or edit license info
          </p>
        </div>

        {/* User Count + Add User */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          <h2 style={{ color: '#333', fontSize: '20px', fontWeight: '600', margin: 0 }}>
            Showing {filteredAndSortedUsers.length} of {users.length} users
          </h2>
          <button
            onClick={openCreateModal}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '5px',
              background: '#4caf50',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + Add User
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
              Search Users
            </label>
            <input
              type="text"
              placeholder="Search by name, email, or license..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
              Filter by Role
            </label>
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Users</option>
              <option value="admins">Admins Only</option>
              <option value="editors">Editors Only</option>
              <option value="users">Regular Users</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            >
              <option value="first_name">First Name</option>
              <option value="last_name">Last Name</option>
              <option value="email">Email</option>
              <option value="created_at">Date Created</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
              Order
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            >
              <option value="asc">A to Z</option>
              <option value="desc">Z to A</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading users...</p>
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '10px', 
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333' }}>First Name</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Last Name</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Email</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Cert Level</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Editor</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Admin</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Status</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>License Info</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Account</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedUsers.map(user => (
                  <tr key={user.id} style={{
                    borderBottom: '1px solid #e0e0e0',
                    opacity: user.is_active === false ? 0.6 : 1,
                    background: user.is_active === false ? '#fafafa' : 'transparent'
                  }}>
                    <td style={{ padding: '15px', color: '#333', fontWeight: '500' }}>
                      {user.first_name || 'Not set'}
                    </td>
                    <td style={{ padding: '15px', color: '#333', fontWeight: '500' }}>
                      {user.last_name || 'Not set'}
                    </td>
                    <td style={{ padding: '15px', color: '#333' }}>{user.email}</td>

                    {/* Cert Level */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <select
                        value={user.certification_level || ''}
                        onChange={(e) => handleCertLevelChange(user.id, e.target.value)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #ccc',
                          fontWeight: user.certification_level ? 600 : 400,
                          background: user.certification_level ? '#e8f5e9' : 'white',
                          color: user.certification_level ? '#2e7d32' : '#888',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">None</option>
                        <option value="EMT">EMT</option>
                        <option value="AEMT">AEMT</option>
                        <option value="Paramedic">Paramedic</option>
                      </select>
                    </td>

                    {/* Editor Toggle */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <div 
                        onClick={() => togglePermission(user.id, 'editor', user.is_editor)}
                        style={{ 
                          position: 'relative', 
                          display: 'inline-block', 
                          width: '50px', 
                          height: '25px',
                          cursor: 'pointer',
                          backgroundColor: user.is_editor ? '#4caf50' : '#ccc',
                          borderRadius: '25px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          height: '19px',
                          width: '19px',
                          left: user.is_editor ? '28px' : '3px',
                          top: '3px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </td>

                    {/* Admin Toggle */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <div 
                        onClick={() => togglePermission(user.id, 'admin', user.is_admin)}
                        style={{ 
                          position: 'relative', 
                          display: 'inline-block', 
                          width: '50px', 
                          height: '25px',
                          cursor: 'pointer',
                          backgroundColor: user.is_admin ? '#2196f3' : '#ccc',
                          borderRadius: '25px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          height: '19px',
                          width: '19px',
                          left: user.is_admin ? '28px' : '3px',
                          top: '3px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: user.is_active === false ? '#ffebee' : '#e8f5e9',
                        color: user.is_active === false ? '#c62828' : '#2e7d32'
                      }}>
                        {user.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                      {user.is_active !== false && user.pin_reset_required && (
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#b26a00', fontWeight: '600' }}>
                          PIN reset pending
                        </div>
                      )}
                    </td>

                    {/* License Info Button */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <button 
                        onClick={() => openLicenseModal(user)}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          background: '#ff9800',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        License Info
                      </button>
                    </td>

                    {/* Account Activate / Deactivate */}
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      {user.id === userInfo?.id ? (
                        <span style={{ fontSize: '12px', color: '#999' }}>-</span>
                      ) : user.is_active === false ? (
                        <button
                          onClick={() => setUserActive(user, true)}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#4caf50',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => setUserActive(user, false)}
                          style={{
                            padding: '6px 12px',
                            border: '2px solid #d32f2f',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#d32f2f',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* License Info Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#333', fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                License Info: {formData.email}
              </h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  State License Number
                </label>
                <input
                  type="text"
                  value={formData.stateLicenseNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, stateLicenseNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  New PIN (6 digits, leave blank to keep current)
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength="6"
                  autoComplete="new-password"
                  value={formData.pin}
                  onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={updateLicenseInfo}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '5px',
                    background: '#ff9800',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Save License Info
                </button>
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    background: 'white',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#333', fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                Add New User
              </h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Employee ID / State License Number
                </label>
                <input
                  type="text"
                  value={createForm.stateLicenseNumber}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, stateLicenseNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  PIN (6 digits)
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  inputMode="numeric"
                  maxLength="6"
                  value={createForm.pin}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Temporary Password (min. 6 characters)
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  value={createForm.password}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                  Certification Level
                </label>
                <select
                  value={createForm.certificationLevel}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, certificationLevel: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">None</option>
                  <option value="EMT">EMT</option>
                  <option value="AEMT">AEMT</option>
                  <option value="Paramedic">Paramedic</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '25px', marginBottom: '30px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#333', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createForm.isEditor}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, isEditor: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Editor
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#333', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createForm.isAdmin}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Admin
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={createUser}
                  disabled={creating}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '5px',
                    background: '#4caf50',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: creating ? 'not-allowed' : 'pointer',
                    opacity: creating ? 0.7 : 1
                  }}
                >
                  {creating ? 'Creating...' : 'Create User'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm(emptyCreateForm);
                  }}
                  disabled={creating}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '5px',
                    background: 'white',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: creating ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;